<template>

     <span>
        <p-search-screen
            ref="searchScreen"
            title="My Reports"
            :filters="filters"
            :fields="fields"
            :filterBy="filterBy"
            :show-bottom-paging="false"
            :show-top-paging="false"
            api-destination="userreports"
        >
            <template v-slot:cell(view)="{ item }">
                <p-button variant="outline-primary"
                        @click.stop="launchReport(item)"
                        size="sm"
                        :is-busy="isBusy">View</p-button>
            </template>
            <template v-slot:cell(rename)="{ item }">
                <p-button variant="outline-primary"
                        @click.stop="showRenameUserReportTemplate(item)"
                        size="sm"
                        :is-busy="isBusy">Rename</p-button>
            </template>
            <template v-slot:cell(delete)="{ item }">
                <p-button variant="outline-danger"
                        @click.stop="deleteUserReportTemplate(item)"
                        size="sm"
                        :is-busy="isBusy">Delete</p-button>
            </template>
        </p-search-screen>

        <p-modal
            ref="renameUserReportTemplateModal"
            name="renameUserReportTemplateModal"
            label="Rename Saved Report"
            size="sm"
            :ok-disabled="!hasRenameTemplateName"
            @ok.prevent="renameUserReportTemplate">
            <template v-slot:default>
                <p-form ref="renameUserReportTemplateForm">
                    <p-input label="Template Name"
                        v-model="renameTemplateName"
                        rules="required|max:50" />
                </p-form>
            </template>
        </p-modal>

        <TotalInventoryReport ref="Total Inventory Value"></TotalInventoryReport>
        <InventoryByDcc ref="Inventory By DCC"></InventoryByDcc>
        <InventoryByFixture ref="Inventory By Fixture"></InventoryByFixture>
        <InventoryNotCounted ref="Inventory Not Counted"></InventoryNotCounted>
        <PriceListReport ref="Price List"></PriceListReport>
        <CourseListReport ref="Course List"></CourseListReport>
        <AuthorListReport ref="Author List" />
        <TextbookListReport ref="Textbook List" />
        <SalesByDccReport ref="Sales By DCC"/>
        <PosDeviceReconciliationReport ref="POS Device Reconciliation"/>
        <CashierReconciliationReport ref="POS Device by Cashier(s) Reconciliation"/>
        <TaxesByJurisdictionReport ref="Sales/Use Tax"/>
        <RentalReconciliationReport ref="Rental Reconciliation"/>
        <RentalStatusReport ref="Rental Status"/>
        <AppleSalesReport ref="Apple Report"/>
        <InvoiceDetailReport ref="Invoice Detail Report"/>
        <WebSalesReport ref="Web Sales and Tax by State"/>
        <ProductSalesReport ref="Product Sales"/>
        <ProductSalesMarginReport ref="Product Sales &amp; Margin"/>
        <SalesByHourReport ref="Sales By Hour"/>
        <NonMerchSalesReport ref="Non-Merch Sales"/>
        <DailySalesReport ref="Daily Sales Report"/>
    </span>
</template>

<script>
import SearchStorage from '@/components/mixins/SearchFilterStorage.js';

import TotalInventoryReport from '@/views/Reports/PhysicalInventory/TotalInventory.vue';
import InventoryByDcc from '@/views/Reports/PhysicalInventory/InventoryByDcc.vue';
import InventoryByFixture from '@/views/Reports/PhysicalInventory/InventoryByFixture.vue';
import InventoryNotCounted from '@/views/Reports/PhysicalInventory/InventoryNotCounted.vue';
import AuthorListReport from '@/views/Reports/Terms/AuthorList.vue';
import CourseListReport from '@/views/Reports/Terms/CourseList.vue';
import PriceListReport from '@/views/Reports/Terms/PriceList.vue';
import TextbookListReport from '@/views/Reports/Terms/TextbookList.vue';
import SalesByDccReport from '@/views/Reports/Sales/SalesByDcc.vue';
import PosDeviceReconciliationReport from '@/views/Reports/Sales/PosDeviceReconciliation.vue';
import CashierReconciliationReport from '@/views/Reports/Sales/CashierReconciliation.vue';
import TaxesByJurisdictionReport from '@/views/Reports/Sales/TaxesByJurisdiction.vue';
import RentalReconciliationReport from '@/views/Reports/Sales/RentalReconciliation.vue';
import RentalStatusReport from '@/views/Reports/Rentals/RentalStatus.vue';
import AppleSalesReport from '@/views/Reports/Sales/AppleSales.vue';
import InvoiceDetailReport from '@/views/Reports/Sales/InvoiceDetailReport.vue';
import WebSalesReport from '@/views/Reports/Sales/WebSales.vue';
import ProductSalesReport from '@/views/Reports/Sales/ProductSales.vue';
import ProductSalesMarginReport from '@/views/Reports/Sales/ProductSalesMargin.vue';
import SalesByHourReport from '@/views/Reports/Sales/SalesByHour.vue';
import NonMerchSalesReport from '@/views/Reports/Sales/NonMerchSales.vue';
import DailySalesReport from '@/views/Reports/Sales/DailySalesReport.vue';

import { datetimeToLocalTimeFormatter } from '@/components/Common/Formatters.js';
import axios from 'axios';

export default {
    mixins: [SearchStorage],
    data() {
        return {
            isBusy: false,
            // Kept distinct from the Reports screen's key so the two lists don't share filters.
            filterName: 'userReportsFilter',
            filters: {
                automatic: "",
                perPage: 100,
                sortBy: 'templateName',
                sortDesc: false,
                searchBy: 'All'
            },
            filterBy: ['All', 'Template Name', 'Name','Category', 'Description'],
            fields: [
                {
                    key: 'view',
                    class: 'tcw100'
                },
                {
                    key: 'templateName',
                    label: 'Template Name',
                    sortable: true
                },
                {
                    key: 'name',
                    label: 'Report',
                    sortable: true
                },
                {
                    key: 'category',
                    sortable: true
                },
                {
                    key: 'description',
                    sortable: true
                },
                {
                    key: 'modifiedDate',
                    label: 'Last Saved',
                    sortable: true,
                    formatter: datetimeToLocalTimeFormatter
                },
                {
                    key: 'rename',
                    label: '',
                    class: 'tcw100'
                },
                {
                    key: 'delete',
                    label: '',
                    class: 'tcw100'
                }
            ],
            // The row the rename modal is working on, and the name being typed into it.
            userReportTemplateBeingRenamed: null,
            renameTemplateName: null
        };
    },
    computed: {
        // Gates the modal's OK button so a saved report can't be renamed to nothing.
        hasRenameTemplateName() {
            return !!this.renameTemplateName?.trim();
        }
    },
    methods: {
        // Every row is a saved template, so launching opens that report's modal and loads the
        // saved parameters into it, leaving the user to review them and pick an export.
        async launchReport(item) {
            if (this.isBusy) 
                return;

            const reportModal = this.$refs[item.name];

            if (!reportModal) {
                this.$appError(`${item.name} cannot be opened from My Reports.`);
                return;
            }

            this.isBusy = true;

            try {
                // show() loads the report's templates, so the saved one can be selected after it.
                await reportModal.show();
                await reportModal.applyUserReportTemplateById(item.userReportTemplateId);
            } finally {
                this.isBusy = false;
            }
        },
        showRenameUserReportTemplate(item) {
            if (this.isBusy) 
                return;

            this.userReportTemplateBeingRenamed = item;
            this.renameTemplateName = item.templateName;
            this.$refs.renameUserReportTemplateModal.show();
        },
        async renameUserReportTemplate() {
            if (this.isBusy) 
                return;

            if (!(await this.$refs.renameUserReportTemplateForm.validate())) {
                return;
            }

            const renamedTemplateName = this.renameTemplateName.trim();

            this.isBusy = true;

            await axios
                .put('/userreports/renameuserreporttemplate', {
                    userReportTemplateId: this.userReportTemplateBeingRenamed.userReportTemplateId,
                    templateName: renamedTemplateName
                })
                .then(() => {
                    this.$appSuccess(`Saved report renamed to '${renamedTemplateName}' successfully.`);
                    this.$refs.renameUserReportTemplateModal.hide();
                    this.$refs.searchScreen.refreshSearchResults();
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        async deleteUserReportTemplate(item) {
            if (this.isBusy) 
                return;

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Please confirm you wish to delete saved report'${item.templateName}'.`);

            if (!confirmed) {
                return;
            }

            this.isBusy = true;

            await axios
                .delete('/userreports/deleteuserreporttemplate', {
                    params: { userReportTemplateId: item.userReportTemplateId }
                })
                .then(() => {
                    this.$appSuccess(`Saved report '${item.templateName}' deleted successfully.`);
                    this.$refs.searchScreen.refreshSearchResults();
                })
                .finally(() => {
                    this.isBusy = false;
                });
        }
    },
    components: {
        TotalInventoryReport,
        InventoryByDcc,
        InventoryByFixture,
        InventoryNotCounted,
        AuthorListReport,
        CourseListReport,
        PriceListReport,
        TextbookListReport,
        SalesByDccReport,
        PosDeviceReconciliationReport,
        CashierReconciliationReport,
        TaxesByJurisdictionReport,
        RentalReconciliationReport,
        RentalStatusReport,
        AppleSalesReport,
        InvoiceDetailReport,
        WebSalesReport,
        ProductSalesReport,
        ProductSalesMarginReport,
        SalesByHourReport,
        NonMerchSalesReport,
        DailySalesReport
    }
};
</script>
<style scoped></style>
